import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  InputAdornment,
  IconButton,
  Tooltip,
  TablePagination,
} from '@mui/material';
import {
  AccountBalance as BalanceIcon,
  TrendingDown as DeductIcon,
  TrendingUp as RefundIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = import.meta.env.VITE_RIDER_API_URL || 'http://localhost:4005';

interface RiderBalance {
  id: string;
  riderId: string;
  riderName: string;
  riderPublicId: string;
  currentBalance: number;
  totalDeposited: number;
  totalDeducted: number;
  totalRefunded: number;
  status: string;
  lastUpdated: string;
}

interface Transaction {
  id: string;
  riderId: string;
  riderName: string;
  riderPublicId: string;
  type: string;
  amount: number;
  status: string;
  reason?: string;
  category?: string;
  createdAt: string;
  approvedBy?: string;
}

interface DashboardStats {
  totalRidersWithSD: number;
  totalSDAmount: number;
  totalDeposited: number;
  totalDeducted: number;
  totalRefunded: number;
  pendingRefunds: number;
}

const SecurityDepositManagement: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dashboard Stats
  const [stats, setStats] = useState<DashboardStats>({
    totalRidersWithSD: 0,
    totalSDAmount: 0,
    totalDeposited: 0,
    totalDeducted: 0,
    totalRefunded: 0,
    pendingRefunds: 0,
  });

  // Balances
  const [balances, setBalances] = useState<RiderBalance[]>([]);
  const [balanceSearch, setBalanceSearch] = useState('');
  const [balancePage, setBalancePage] = useState(0);
  const [balanceRowsPerPage, setBalanceRowsPerPage] = useState(10);

  // Transactions
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [transactionSearch, setTransactionSearch] = useState('');
  const [transactionPage, setTransactionPage] = useState(0);
  const [transactionRowsPerPage, setTransactionRowsPerPage] = useState(10);

  // Deduction Dialog
  const [deductDialogOpen, setDeductDialogOpen] = useState(false);
  const [selectedRider, setSelectedRider] = useState<RiderBalance | null>(null);
  const [deductionForm, setDeductionForm] = useState({
    amount: '',
    reason: '',
    category: 'DAMAGE',
    requiresApproval: false,
  });

  // Refund Dialog
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundForm, setRefundForm] = useState({
    amount: '',
    bankAccountNumber: '',
    bankIFSC: '',
    vehicleCondition: 'good',
    remarks: '',
  });

  useEffect(() => {
    loadDashboardStats();
    loadBalances();
    loadTransactions();
  }, []);

  const loadDashboardStats = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/v1/security-deposit/balances/summary`);
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error: any) {
      console.error('Failed to load dashboard stats:', error);
    }
  };

  const loadBalances = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/v1/security-deposit/balances`);
      if (response.data.success) {
        setBalances(response.data.data);
      }
    } catch (error: any) {
      setError('Failed to load rider balances: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/v1/security-deposit/transactions`);
      if (response.data.success) {
        setTransactions(response.data.data);
      }
    } catch (error: any) {
      setError('Failed to load transactions: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeductClick = (rider: RiderBalance) => {
    setSelectedRider(rider);
    setDeductionForm({
      amount: '',
      reason: '',
      category: 'DAMAGE',
      requiresApproval: false,
    });
    setDeductDialogOpen(true);
  };

  const handleRefundClick = (rider: RiderBalance) => {
    setSelectedRider(rider);
    setRefundForm({
      amount: rider.currentBalance.toString(),
      bankAccountNumber: '',
      bankIFSC: '',
      vehicleCondition: 'good',
      remarks: '',
    });
    setRefundDialogOpen(true);
  };

  const handleDeductSubmit = async () => {
    if (!selectedRider) return;

    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE_URL}/v1/security-deposit/deduct`, {
        riderId: selectedRider.riderId,
        amount: parseFloat(deductionForm.amount),
        reason: deductionForm.reason,
        category: deductionForm.category,
        requiresApproval: deductionForm.requiresApproval,
      });

      if (response.data.success) {
        setSuccess('Security deposit deduction successful');
        setDeductDialogOpen(false);
        loadBalances();
        loadTransactions();
        loadDashboardStats();
      }
    } catch (error: any) {
      setError('Deduction failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRefundSubmit = async () => {
    if (!selectedRider) return;

    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE_URL}/v1/security-deposit/refund/initiate`, {
        riderId: selectedRider.riderId,
        amount: parseFloat(refundForm.amount),
        bankDetails: {
          accountNumber: refundForm.bankAccountNumber,
          ifscCode: refundForm.bankIFSC,
        },
        vehicleCondition: refundForm.vehicleCondition,
        remarks: refundForm.remarks,
      });

      if (response.data.success) {
        setSuccess('Refund initiated successfully. Pending approval.');
        setRefundDialogOpen(false);
        loadBalances();
        loadTransactions();
        loadDashboardStats();
      }
    } catch (error: any) {
      setError('Refund initiation failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'COMPLETED':
        return 'success';
      case 'FAILED':
        return 'error';
      case 'REFUNDED':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type?.toUpperCase()) {
      case 'PAYMENT':
        return 'success';
      case 'DEDUCTION':
        return 'warning';
      case 'REFUND':
        return 'info';
      default:
        return 'default';
    }
  };

  const filteredBalances = balances.filter(
    (b) =>
      b.riderName?.toLowerCase().includes(balanceSearch.toLowerCase()) ||
      b.riderPublicId?.toLowerCase().includes(balanceSearch.toLowerCase())
  );

  const filteredTransactions = transactions.filter(
    (t) =>
      t.riderName?.toLowerCase().includes(transactionSearch.toLowerCase()) ||
      t.riderPublicId?.toLowerCase().includes(transactionSearch.toLowerCase())
  );

  const paginatedBalances = filteredBalances.slice(
    balancePage * balanceRowsPerPage,
    balancePage * balanceRowsPerPage + balanceRowsPerPage
  );

  const paginatedTransactions = filteredTransactions.slice(
    transactionPage * transactionRowsPerPage,
    transactionPage * transactionRowsPerPage + transactionRowsPerPage
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" fontWeight="bold">
          Security Deposit Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            loadDashboardStats();
            loadBalances();
            loadTransactions();
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Dashboard Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Riders with SD
                  </Typography>
                  <Typography variant="h4">{stats.totalRidersWithSD}</Typography>
                </Box>
                <BalanceIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total SD Amount
                  </Typography>
                  <Typography variant="h4">₹{stats.totalSDAmount.toLocaleString()}</Typography>
                </Box>
                <BalanceIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Pending Refunds
                  </Typography>
                  <Typography variant="h4">{stats.pendingRefunds}</Typography>
                </Box>
                <RefundIcon sx={{ fontSize: 48, color: 'warning.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Deposited
              </Typography>
              <Typography variant="h5" color="success.main">
                ₹{stats.totalDeposited.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Deducted
              </Typography>
              <Typography variant="h5" color="warning.main">
                ₹{stats.totalDeducted.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Refunded
              </Typography>
              <Typography variant="h5" color="info.main">
                ₹{stats.totalRefunded.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Rider Balances" icon={<BalanceIcon />} iconPosition="start" />
          <Tab label="Transactions" icon={<ReceiptIcon />} iconPosition="start" />
        </Tabs>

        {/* Tab 0: Rider Balances */}
        {activeTab === 0 && (
          <CardContent>
            <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                placeholder="Search by rider name or ID..."
                value={balanceSearch}
                onChange={(e) => setBalanceSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Rider</TableCell>
                    <TableCell>Rider ID</TableCell>
                    <TableCell align="right">Current Balance</TableCell>
                    <TableCell align="right">Total Deposited</TableCell>
                    <TableCell align="right">Total Deducted</TableCell>
                    <TableCell align="right">Total Refunded</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedBalances.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No rider balances found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedBalances.map((balance) => (
                      <TableRow key={balance.id}>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ cursor: 'pointer', color: 'primary.main' }}
                            onClick={() => navigate(`/rider-management/${balance.riderId}`)}
                          >
                            {balance.riderName || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>{balance.riderPublicId}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">₹{balance.currentBalance.toLocaleString()}</Typography>
                        </TableCell>
                        <TableCell align="right">₹{balance.totalDeposited.toLocaleString()}</TableCell>
                        <TableCell align="right">₹{balance.totalDeducted.toLocaleString()}</TableCell>
                        <TableCell align="right">₹{balance.totalRefunded.toLocaleString()}</TableCell>
                        <TableCell>
                          <Chip label={balance.status} color={getStatusColor(balance.status)} size="small" />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Deduct">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleDeductClick(balance)}
                              disabled={balance.currentBalance <= 0}
                            >
                              <DeductIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Refund">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleRefundClick(balance)}
                              disabled={balance.currentBalance <= 0}
                            >
                              <RefundIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredBalances.length}
              page={balancePage}
              onPageChange={(_, newPage) => setBalancePage(newPage)}
              rowsPerPage={balanceRowsPerPage}
              onRowsPerPageChange={(e) => {
                setBalanceRowsPerPage(parseInt(e.target.value, 10));
                setBalancePage(0);
              }}
            />
          </CardContent>
        )}

        {/* Tab 1: Transactions */}
        {activeTab === 1 && (
          <CardContent>
            <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                placeholder="Search by rider name or ID..."
                value={transactionSearch}
                onChange={(e) => setTransactionSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Rider</TableCell>
                    <TableCell>Rider ID</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTransactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No transactions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedTransactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>{new Date(transaction.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ cursor: 'pointer', color: 'primary.main' }}
                            onClick={() => navigate(`/rider-management/${transaction.riderId}`)}
                          >
                            {transaction.riderName || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>{transaction.riderPublicId}</TableCell>
                        <TableCell>
                          <Chip label={transaction.type} color={getTypeColor(transaction.type)} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">₹{transaction.amount.toLocaleString()}</Typography>
                        </TableCell>
                        <TableCell>{transaction.reason || '-'}</TableCell>
                        <TableCell>{transaction.category || '-'}</TableCell>
                        <TableCell>
                          <Chip label={transaction.status} color={getStatusColor(transaction.status)} size="small" />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredTransactions.length}
              page={transactionPage}
              onPageChange={(_, newPage) => setTransactionPage(newPage)}
              rowsPerPage={transactionRowsPerPage}
              onRowsPerPageChange={(e) => {
                setTransactionRowsPerPage(parseInt(e.target.value, 10));
                setTransactionPage(0);
              }}
            />
          </CardContent>
        )}
      </Card>

      {/* Deduction Dialog */}
      <Dialog open={deductDialogOpen} onClose={() => setDeductDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Deduct Security Deposit</DialogTitle>
        <DialogContent>
          {selectedRider && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Rider: {selectedRider.riderName} ({selectedRider.riderPublicId})
                <br />
                Current Balance: ₹{selectedRider.currentBalance.toLocaleString()}
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Amount to Deduct"
                    type="number"
                    value={deductionForm.amount}
                    onChange={(e) => setDeductionForm({ ...deductionForm, amount: e.target.value })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={deductionForm.category}
                      onChange={(e) => setDeductionForm({ ...deductionForm, category: e.target.value })}
                      label="Category"
                    >
                      <MenuItem value="DAMAGE">Damage</MenuItem>
                      <MenuItem value="VIOLATION">Violation</MenuItem>
                      <MenuItem value="PENALTY">Penalty</MenuItem>
                      <MenuItem value="LATE_FEE">Late Fee</MenuItem>
                      <MenuItem value="OTHER">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Reason"
                    multiline
                    rows={3}
                    value={deductionForm.reason}
                    onChange={(e) => setDeductionForm({ ...deductionForm, reason: e.target.value })}
                    required
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeductDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeductSubmit}
            variant="contained"
            color="warning"
            disabled={!deductionForm.amount || !deductionForm.reason || loading}
          >
            Deduct
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={refundDialogOpen} onClose={() => setRefundDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Initiate Security Deposit Refund</DialogTitle>
        <DialogContent>
          {selectedRider && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Rider: {selectedRider.riderName} ({selectedRider.riderPublicId})
                <br />
                Current Balance: ₹{selectedRider.currentBalance.toLocaleString()}
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Refund Amount"
                    type="number"
                    value={refundForm.amount}
                    onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Bank Account Number"
                    value={refundForm.bankAccountNumber}
                    onChange={(e) => setRefundForm({ ...refundForm, bankAccountNumber: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Bank IFSC Code"
                    value={refundForm.bankIFSC}
                    onChange={(e) => setRefundForm({ ...refundForm, bankIFSC: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Vehicle Condition</InputLabel>
                    <Select
                      value={refundForm.vehicleCondition}
                      onChange={(e) => setRefundForm({ ...refundForm, vehicleCondition: e.target.value })}
                      label="Vehicle Condition"
                    >
                      <MenuItem value="excellent">Excellent</MenuItem>
                      <MenuItem value="good">Good</MenuItem>
                      <MenuItem value="fair">Fair</MenuItem>
                      <MenuItem value="poor">Poor</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Remarks"
                    multiline
                    rows={3}
                    value={refundForm.remarks}
                    onChange={(e) => setRefundForm({ ...refundForm, remarks: e.target.value })}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRefundSubmit}
            variant="contained"
            color="info"
            disabled={
              !refundForm.amount || !refundForm.bankAccountNumber || !refundForm.bankIFSC || loading
            }
          >
            Initiate Refund
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SecurityDepositManagement;
